import { Component, DestroyRef, Input, OnChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { OptionsListMulti } from 'gung-common';
import { catchError, of } from 'rxjs';
import {
  SalesInsightsFilterOption,
  SalesInsightsFilterValues,
  SalesInsightsPeriod
} from '../../models/sales-insights.model';
import { SalesInsightsDashboardState } from '../../services/sales-insights-dashboard-state.service';
import { SalesInsightsService } from '../../services/sales-insights.service';
import { salesInsightsFilterLabel } from '../../utils/sales-insights-labels';

export interface SalesInsightsFilterField {
  key: string;
  label: string;
  options: OptionsListMulti[];
}

const PERIOD_FILTER = 'period';
const PERIODS: SalesInsightsPeriod[] = ['ytd', 'mtd', 'qtd', 'r3', 'r12'];
const ALL = '';

@Component({
  selector: 'lib-sales-insights-filter-bar',
  standalone: false,
  templateUrl: './sales-insights-filter-bar.component.html',
  styleUrl: './sales-insights-filter-bar.component.scss'
})
export class SalesInsightsFilterBarComponent implements OnChanges {
  @Input() filters: string[] = [];

  protected salesInsightsService = inject(SalesInsightsService);
  protected dashboardState = inject(SalesInsightsDashboardState);
  protected translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  fields: SalesInsightsFilterField[] = [];

  ngOnChanges(): void {
    this.fields = (this.filters || []).map(f => (f === PERIOD_FILTER ? this.periodField() : this.field(f)));
  }

  select(key: string, selected: string[]): void {
    const value = selected?.[0] || ALL;
    this.dashboardState.setFilters(this.applyFilter(this.dashboardState.filters, key, value));
  }

  private applyFilter(filters: SalesInsightsFilterValues, key: string, value: string): SalesInsightsFilterValues {
    const next: SalesInsightsFilterValues = { ...filters, dims: { ...(filters.dims || {}) } };
    if (key === PERIOD_FILTER) {
      next.period = (value as SalesInsightsPeriod) || undefined;
    } else if (key === 'customer') {
      next.customerId = value ? [value] : undefined;
    } else if (key === 'salesperson') {
      next.salesperson = value || undefined;
    } else if (key === 'country') {
      next.country = value || undefined;
    } else if (this.salesInsightsService.isDimensionFilter(key)) {
      const name = this.salesInsightsService.dimensionName(key);
      if (value) {
        next.dims[name] = value;
      } else {
        delete next.dims[name];
      }
    }
    return next;
  }

  private periodField(): SalesInsightsFilterField {
    return {
      key: PERIOD_FILTER,
      label: this.translate.instant('SALES_INSIGHTS_FILTER_PERIOD'),
      options: this.toOptions(
        'SALES_INSIGHTS_PERIOD_DEFAULT',
        PERIODS.map(p => ({ id: p, name: this.translate.instant(`SALES_INSIGHTS_PERIOD_${p.toUpperCase()}`) }))
      )
    };
  }

  private field(key: string): SalesInsightsFilterField {
    const field: SalesInsightsFilterField = {
      key,
      label: salesInsightsFilterLabel(this.translate, key),
      options: this.toOptions('SALES_INSIGHTS_ALL', [])
    };
    this.salesInsightsService
      .getFilterOptions(key)
      .pipe(
        catchError(() => of([] as SalesInsightsFilterOption[])),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(options => (field.options = this.toOptions('SALES_INSIGHTS_ALL', this.withCodes(options))));
    return field;
  }

  private withCodes(options: SalesInsightsFilterOption[]): SalesInsightsFilterOption[] {
    return options
      .map(option => ({
        id: option.id,
        name: option.name && option.name !== option.id ? `${option.name} (${option.id})` : option.id
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }

  private toOptions(allLabelKey: string, options: SalesInsightsFilterOption[]): OptionsListMulti[] {
    return [
      { id: ALL, name: this.translate.instant(allLabelKey), selected: true },
      ...options.map(option => ({ ...option, selected: false }))
    ];
  }
}
